import { useSyncExternalStore } from 'react';

import { isLook, lookAttribute, lookFromSearch, readLook, saveLook, type Look } from './look';

/**
 * One look for the whole document, applied before the first paint (main.tsx)
 * so the panel never flashes the old style, and changed from the sidebar.
 */

const listeners = new Set<() => void>();
let current: Look = initialLook();

function browserStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/** A `?look=` in the address wins and is remembered; otherwise the device's saved choice. */
function initialLook(): Look {
  const asked = lookFromSearch(window.location.search);
  if (!asked) return readLook(browserStorage());
  saveLook(browserStorage(), asked);
  forgetLookInAddress();
  return asked;
}

/** Once saved, the link's look leaves the address, so reloading keeps whatever the person picks next. */
function forgetLookInAddress(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('look');
  window.history.replaceState(window.history.state, '', url);
}

export function applyLook(look: Look = current): void {
  document.documentElement.dataset.look = lookAttribute(look);
}

export function setLook(look: Look): void {
  current = look;
  saveLook(browserStorage(), look);
  applyLook(look);
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useLook(): Look {
  return useSyncExternalStore(subscribe, () => current, () => current);
}

/** The event the demo's floating switcher dispatches on window, with the look's name as `detail`. */
export const LOOK_REQUEST_EVENT = 'dale:look';

/** Applies a look asked for from outside the panel (the sales demo), exactly as Configuración would. */
export function listenForLookRequests(target: Window = window): () => void {
  const onRequest = (event: Event) => {
    const look = (event as CustomEvent<unknown>).detail;
    if (isLook(look)) setLook(look);
  };
  target.addEventListener(LOOK_REQUEST_EVENT, onRequest);
  return () => target.removeEventListener(LOOK_REQUEST_EVENT, onRequest);
}
